import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, limit, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { slugify } from '../utils/slugify';
import { decodeArticleTrackingPack } from '../utils/articleTrackingLink';
import DOMPurify from 'dompurify';

function normalizeTrackingParam(v) {
  if (v == null) return null;
  const t = String(v).trim();
  return t ? t : null;
}

/** Collect URLSearchParams from RR, window.search, and hash query (SMS / in-app browsers vary). */
function collectArticleTrackingParamSources(primarySearchParams) {
  const list = [];
  if (primarySearchParams) {
    list.push(primarySearchParams);
  }
  if (typeof window !== 'undefined' && window.location.search) {
    list.push(new URLSearchParams(window.location.search));
  }
  if (typeof window !== 'undefined' && window.location.hash) {
    const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const qPart = raw.includes('?') ? raw.split('?').slice(1).join('?') : raw;
    if (qPart && qPart.includes('=')) {
      list.push(new URLSearchParams(qPart.startsWith('&') ? qPart.slice(1) : qPart));
    }
  }
  return list;
}

/** Merge d/m/r and packed ?x= from all sources; optional /article/:slug/t/:trackPack segment. */
function parseArticleTrackingFromLocation(searchParams, pathTrackPack) {
  let discipleId = null;
  let mentorAppID = null;
  let resourceId = null;

  const pathRaw = normalizeTrackingParam(pathTrackPack);
  if (pathRaw) {
    let pack = decodeArticleTrackingPack(pathRaw);
    if (!pack) {
      try {
        pack = decodeArticleTrackingPack(decodeURIComponent(pathRaw));
      } catch (_) {
        /* ignore */
      }
    }
    if (pack) {
      discipleId = pack.discipleId;
      mentorAppID = pack.mentorAppID;
      resourceId = pack.resourceId;
    }
  }

  const sources = collectArticleTrackingParamSources(searchParams);

  for (const sp of sources) {
    discipleId = discipleId || normalizeTrackingParam(sp.get('d'));
    mentorAppID = mentorAppID || normalizeTrackingParam(sp.get('m'));
    resourceId = resourceId || normalizeTrackingParam(sp.get('r'));
    const xRaw = sp.get('x') || sp.get('X');
    if (xRaw) {
      const pack = decodeArticleTrackingPack(xRaw);
      if (pack) {
        discipleId = discipleId || pack.discipleId;
        mentorAppID = mentorAppID || pack.mentorAppID;
        resourceId = resourceId || pack.resourceId;
      }
    }
  }

  return { discipleId, mentorAppID, resourceId };
}

const CHILDREN_CATEGORIES = [
  'Bible Stories',
  'Relationships',
  'Life Questions',
  'Identity',
  'All about God',
  'Life Principles'
];

const ARTICLE_TRACKING_STORAGE_PREFIX = 'tom_article_tracking_v1:';

/** Slug vs Firestore id in the URL can differ; try every stable key so sessionStorage replay works. */
function articleTrackingStorageKeyList(routeArticleId, articleDoc) {
  const keys = [];
  if (routeArticleId) keys.push(String(routeArticleId).trim());
  if (articleDoc?.slug) keys.push(String(articleDoc.slug).trim());
  if (articleDoc?.id) keys.push(String(articleDoc.id).trim());
  return [...new Set(keys.filter(Boolean))];
}

function readStoredArticleTracking(articleRouteKey) {
  if (typeof window === 'undefined' || !articleRouteKey) return null;
  try {
    const raw = sessionStorage.getItem(ARTICLE_TRACKING_STORAGE_PREFIX + articleRouteKey);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o && o.d && o.m && o.r) return { discipleId: o.d, mentorAppID: o.m, resourceId: o.r };
  } catch (_) {
    /* ignore */
  }
  return null;
}

function readStoredArticleTrackingFirstMatch(routeArticleId, articleDoc) {
  const keys = articleTrackingStorageKeyList(routeArticleId, articleDoc);
  for (const k of keys) {
    const v = readStoredArticleTracking(k);
    if (v) return v;
  }
  return null;
}

function writeStoredArticleTracking(articleRouteKey, params) {
  if (typeof window === 'undefined' || !articleRouteKey) return;
  if (!params.discipleId || !params.mentorAppID || !params.resourceId) return;
  try {
    sessionStorage.setItem(
      ARTICLE_TRACKING_STORAGE_PREFIX + articleRouteKey,
      JSON.stringify({ d: params.discipleId, m: params.mentorAppID, r: params.resourceId })
    );
  } catch (_) {
    /* private mode / quota */
  }
}

function writeStoredArticleTrackingForArticle(routeArticleId, articleDoc, params) {
  for (const k of articleTrackingStorageKeyList(routeArticleId, articleDoc)) {
    writeStoredArticleTracking(k, params);
  }
}

const Article = () => {
  const { articleId, trackPack } = useParams();
  const [searchParams] = useSearchParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isChildrenTrack, setIsChildrenTrack] = useState(false);

  const [markingFinished, setMarkingFinished] = useState(false);
  const [finished, setFinished] = useState(false);
  
  // Get tracking parameters from URL (with fallbacks for mobile / naked article links)
  const [trackingParams, setTrackingParams] = useState(() => {
    try {
      if (typeof window === 'undefined') {
        return { discipleId: null, mentorAppID: null, resourceId: null };
      }
      const fromUrl = parseArticleTrackingFromLocation(null, trackPack);
      const stored = readStoredArticleTrackingFirstMatch(articleId || '', null);
      return {
        discipleId: fromUrl.discipleId || stored?.discipleId || null,
        mentorAppID: fromUrl.mentorAppID || stored?.mentorAppID || null,
        resourceId: fromUrl.resourceId || stored?.resourceId || null,
      };
    } catch (e) {
      return { discipleId: null, mentorAppID: null, resourceId: null };
    }
  });

  useEffect(() => {
    const syncTrackingFromLocation = () => {
      try {
        const fromUrl = parseArticleTrackingFromLocation(searchParams, trackPack);
        const stored = readStoredArticleTrackingFirstMatch(articleId || '', article);
        const merged = {
          discipleId: fromUrl.discipleId || stored?.discipleId || null,
          mentorAppID: fromUrl.mentorAppID || stored?.mentorAppID || null,
          resourceId: fromUrl.resourceId || stored?.resourceId || null,
        };
        setTrackingParams(merged);
      } catch (e) {
        console.error('Error parsing URL params:', e);
      }
    };

    syncTrackingFromLocation();
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', syncTrackingFromLocation);
      return () => window.removeEventListener('hashchange', syncTrackingFromLocation);
    }
    return undefined;
  }, [searchParams, articleId, article, trackPack]);

  // Firestore articles store resourceId; naked tmrsrc.com links often omit ?r= — fill it so "Finished" can show.
  useEffect(() => {
    if (!article) return;
    setTrackingParams((prev) => {
      const fromArticle = article.resourceId != null && String(article.resourceId).trim()
        ? String(article.resourceId).trim()
        : null;
      const resourceId = prev.resourceId || fromArticle;
      const next = {
        ...prev,
        resourceId: resourceId || null,
      };
      if (next.discipleId && next.mentorAppID && next.resourceId) {
        writeStoredArticleTrackingForArticle(articleId, article, next);
      }
      return next;
    });
  }, [article, articleId]);

  const { discipleId, mentorAppID, resourceId } = trackingParams;
  const showFinishedButton = discipleId && mentorAppID && resourceId;

  // Load article and determine if it's a children's article
  useEffect(() => {
    const loadArticle = async () => {
      if (!articleId) return;

      try {
        setLoading(true);
        setError(null);
        
        let articleSnap = null;

        // Try slug lookup first
        const slugQuery = query(
          collection(db, 'articles'),
          where('slug', '==', articleId)
        );
        const slugResults = await getDocs(slugQuery);

        if (!slugResults.empty) {
          articleSnap = slugResults.docs[0];
        } else {
          // Fall back to direct Firestore doc ID lookup (backward compat)
          const directRef = doc(db, 'articles', articleId);
          const directSnap = await getDoc(directRef);
          if (directSnap.exists()) {
            articleSnap = directSnap;
          }
        }

        // Fallback: find by slugified title (or doc's slug), including ever/every-style variants
        if ((!articleSnap || !articleSnap.exists()) && articleId && articleId.includes('-')) {
          const slugVariants = [
            articleId,
            articleId.replace(/ever/g, 'every'),
            articleId.replace(/every/g, 'ever')
          ];
          const recentQuery = query(
            collection(db, 'articles'),
            limit(200)
          );
          const recentSnap = await getDocs(recentQuery);
          const match = recentSnap.docs.find(d => {
            const data = d.data();
            const titleSlug = slugify(data.title || '');
            const docSlug = data.slug || '';
            return slugVariants.includes(titleSlug) || slugVariants.includes(docSlug);
          });
          if (match) {
            articleSnap = match;
            // Backfill slug so future requests use the fast path
            updateDoc(doc(db, 'articles', match.id), { slug: articleId }).catch(() => {});
          }
        }

        if (!articleSnap || !articleSnap.exists()) {
          setError('Article not found');
          setLoading(false);
          return;
        }

        const articleData = articleSnap.data();
        
        // Check if track is a children's track - MUST happen before setArticle
        let childrenTrackCheck = false;
        if (articleData.trackId) {
          try {
            const trackRef = doc(db, 'contentTracks', articleData.trackId);
            const trackSnap = await getDoc(trackRef);
            if (trackSnap.exists()) {
              const trackData = trackSnap.data();
              childrenTrackCheck = trackData.trackType === 'children';
            }
          } catch (trackError) {
            console.warn('Could not check track type:', trackError);
          }
        }

        // Check category
        const hasChildrenCategory = articleData.category && CHILDREN_CATEGORIES.includes(articleData.category);
        const detectedAsChildren = hasChildrenCategory || childrenTrackCheck;

        setIsChildrenTrack(childrenTrackCheck);
        
        setArticle({
            id: articleSnap.id,
            ...articleData,
          });
      } catch (err) {
        console.error('Error loading article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

      loadArticle();
  }, [articleId]);

  // Check if resource is already completed
  useEffect(() => {
    const checkCompletionStatus = async () => {
      if (!showFinishedButton || !discipleId || !mentorAppID || !resourceId) return;

      try {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID);
        const mentorDetailsDoc = await getDoc(mentorDetailsRef);

        if (mentorDetailsDoc.exists()) {
          const mentorDetails = mentorDetailsDoc.data();
          const existingProgress = mentorDetails?.contentTrackProgress;
          const completedResourceIds = existingProgress?.completedResourceIds || [];
          
          if (completedResourceIds.includes(resourceId)) {
            setFinished(true);
          }
        }
      } catch (error) {
        console.error('Error checking completion status:', error);
      }
    };

    checkCompletionStatus();
  }, [showFinishedButton, discipleId, mentorAppID, resourceId]);

  // Update page title and meta tags
  useEffect(() => {
    if (!article) return;

    document.title = article.title || 'Article - Together On Mission';

    const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
    const articleUrl = `${articleDomain}/article/${article.slug || articleId}`;
    const description = article.description || 
      (article.content 
        ? article.content.substring(0, 160).replace(/\n/g, ' ').trim() + '...'
        : 'Read this article on Together On Mission');
    const logoUrl = `${articleDomain}/images/TOM-logo-512.png`;

    const updateMetaTag = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || 
                 document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateNameMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('og:title', article.title || 'Article - Together On Mission');
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', logoUrl);
    updateMetaTag('og:url', articleUrl);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:site_name', 'Together On Mission');

    updateNameMetaTag('twitter:card', 'summary_large_image');
    updateNameMetaTag('twitter:title', article.title || 'Article - Together On Mission');
    updateNameMetaTag('twitter:description', description);
    updateNameMetaTag('twitter:image', logoUrl);
    updateNameMetaTag('description', description);

    return () => {
      document.title = 'Together On Mission';
    };
  }, [article, articleId]);

  // Mark article as finished and record a mentor-visible completion notice on mentorDetails
  const handleFinished = async () => {
    if (!discipleId || !mentorAppID || !resourceId) return;

    setMarkingFinished(true);
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID);
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);

      if (!mentorDetailsDoc.exists()) {
        console.error('Mentor details not found');
        alert('Unable to track completion. Please contact your mentor.');
        return;
      }

      const mentorDetails = mentorDetailsDoc.data();
      const existingProgress = mentorDetails?.contentTrackProgress;

      if (!existingProgress) {
        console.warn('No content track progress found.');
        alert('Unable to track completion. Progress tracking not found.');
        return;
      }

      const completedResourceIds = existingProgress.completedResourceIds || [];
      const isNewCompletion = !completedResourceIds.includes(resourceId);
      const updatedCompletedResourceIds = isNewCompletion
        ? [...completedResourceIds, resourceId]
        : completedResourceIds;

      const updates = {
        'contentTrackProgress.completedResourceIds': updatedCompletedResourceIds,
        'contentTrackProgress.lastUpdated': serverTimestamp(),
        lastInteractionAt: serverTimestamp(),
      };

      if (isNewCompletion) {
        let resourceTitle = article?.title || 'Resource';
        if (article?.trackId) {
          try {
            const resSnap = await getDoc(
              doc(db, 'contentTracks', article.trackId, 'resources', resourceId)
            );
            if (resSnap.exists() && resSnap.data()?.title) {
              resourceTitle = resSnap.data().title;
            }
          } catch (e) {
            console.warn('Could not load resource title for completion notice:', e);
          }
        }

        // Do not read disciples/{id} from this public page—rules (and sessions) vary; mentor already knows who this is.
        const discipleFirstName = 'Your disciple';
        const text = `${discipleFirstName} completed ${resourceTitle}.`;
        updates.completionNotices = arrayUnion({
          text,
          resourceId,
          resourceTitle,
          createdAt: Timestamp.now(),
        });
      }

      await updateDoc(mentorDetailsRef, updates);

      setFinished(true);
    } catch (error) {
      console.error('Error marking article as finished:', error);
      alert('Unable to track completion. Please try again.');
    } finally {
      setMarkingFinished(false);
    }
  };

  // Support both 'content' (new format) and 'text' (old format) fields
  const articleContent = article?.content || article?.text || '';

  // Detect HTML content (from WYSIWYG editor) vs plain text (legacy)
  const isHtmlContent = useMemo(() => {
    return /<[a-z][\s\S]*>/i.test(articleContent);
  }, [articleContent]);

  const sanitizedHtml = useMemo(() => {
    if (!isHtmlContent || !articleContent) return '';
    // Replace non-breaking spaces with regular spaces so text can wrap naturally at word boundaries
    const withNormalSpaces = articleContent.replace(/\u00A0/g, ' ').replace(/&nbsp;/gi, ' ');
    // Respect carriage returns: newlines inside HTML become <br /> so paragraphs and line breaks display
    const withBreaks = withNormalSpaces.replace(/\n/g, '<br />');
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    const clean = DOMPurify.sanitize(withBreaks, {
      ALLOWED_TAGS: ['p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ol', 'ul', 'li', 'blockquote', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title'],
    });
    DOMPurify.removeHook('afterSanitizeAttributes');
    return clean;
  }, [articleContent, isHtmlContent]);

  // Plain text fallback for legacy articles
  const formattedContent = (!isHtmlContent && articleContent)
    ? articleContent.split('\n').map((paragraph, index) => (
        paragraph.trim() ? (
          <p key={index} className="mb-4 leading-7">
            {paragraph}
          </p>
        ) : (
          <br key={index} />
        )
      ))
    : null;

  // Determine if this is a children's article - use stored values from loadArticle
  const hasChildrenCategory = article?.category && CHILDREN_CATEGORIES.includes(article.category);
  const isChildrensArticle = hasChildrenCategory || isChildrenTrack;
  
  // Get image source - check both fields with more robust checks
  const imageURLValue = article?.imageURL;
  const imageValue = article?.image;
  const imageSrc = 
    (typeof imageURLValue === 'string' && imageURLValue.trim()) || 
    (typeof imageValue === 'string' && imageValue.trim()) || 
    '';
  const hasImage = !!imageSrc && imageSrc.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
          <p className="text-gray-600">{error || 'The article you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  // CHILDREN'S ARTICLE LAYOUT - Mobile: Sticky image top 50vh, scrollable content bottom 50vh
  if (isChildrensArticle) {
    return (
      <div className="h-screen flex flex-col bg-white overflow-hidden md:min-h-screen md:h-auto">
        {/* Fixed Image Section - Top 50% on mobile, normal on desktop */}
        <div className="fixed top-0 left-0 right-0 h-[50vh] z-10 bg-gray-100 md:relative md:h-auto md:z-auto">
          <div className="w-full h-full relative flex items-center justify-center">
            {hasImage && imageSrc ? (
              <>
                <img
                  src={imageSrc}
                  alt={article.title || 'Article image'}
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {article.subtitle?.trim() && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent p-4 md:bg-transparent md:relative md:mt-3 md:bg-none">
                    <p className="text-white text-sm italic text-center md:text-gray-600 md:text-base">
                      {article.subtitle}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center px-4">
                <div className="text-6xl mb-4">📖</div>
                {article.subtitle?.trim() && (
                  <p className="text-xl text-gray-600 italic">
                    {article.subtitle}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Content Section - Bottom 50% on mobile, normal on desktop */}
        <div className="mt-[50vh] flex-1 overflow-y-auto md:mt-0 md:overflow-visible bg-white">
          <main className="article-page w-full max-w-3xl mx-auto px-4 sm:px-6 py-6 md:py-12 box-border">
            <article className="max-w-full">
              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {article.title}
              </h1>

              {/* Author */}
              {(article.author || article.authorWebsite || article.authorName) && (
                <div className="article-byline mb-6 md:mb-8 pb-4 border-b border-gray-200">
                  <p className="text-sm text-gray-400">
                    {(article.author || article.authorName) && <span>{article.author || article.authorName}</span>}
                    {(article.author || article.authorName) && (article.authorWebsite || article.website) && <span className="mx-1.5">·</span>}
                    {(article.authorWebsite || article.website) && (
                      <a 
                        href={article.authorWebsite || article.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-500 transition-colors no-underline"
                      >
                        {(article.authorWebsite || article.website).replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    )}
                  </p>
                </div>
              )}

              {/* Article Content - prose typography, responsive */}
              <div className="article-content prose prose-lg prose-gray max-w-none [&_img]:max-w-full [&_img]:h-auto [&_pre]:overflow-x-auto">
                {isHtmlContent ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
                ) : formattedContent || (
                  <p className="text-gray-500 italic">No content available for this article.</p>
                )}
              </div>

              {/* Used by Permission */}
              <div className="article-footer mt-8 md:mt-12 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-400 text-center italic">
                  Used by permission
                </p>
              </div>

              {/* Finished Button */}
              {showFinishedButton && (
                <div className="mt-6 md:mt-8 pt-6 border-t border-gray-200 pb-8">
                  {finished ? (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-medium">Marked as Finished</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <button
                        onClick={handleFinished}
                        disabled={markingFinished}
                        className="inline-flex items-center px-6 py-3 bg-[#5ABA47] hover:bg-[#4a9939] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-md"
                      >
                        {markingFinished ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Finished
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!showFinishedButton && article?.trackId && (
                <div className="mt-6 pt-4 pb-8 text-center text-xs text-gray-500 max-w-lg mx-auto px-2 leading-relaxed">
                  To mark this lesson finished, open the <strong className="font-medium text-gray-600">exact link</strong> your mentor sent from Together On Mission. Tracked links include <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">/t/</span> in the path and/or <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">?</span> or <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">#</span> with codes after them. Tap the link in the message—preview screens often open a plain <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">/article/…</span> URL that cannot record progress.
                </div>
              )}
            </article>
          </main>
        </div>
      </div>
    );
  }

  // STANDARD ARTICLE LAYOUT - For non-children's articles
  return (
    <div className="min-h-screen bg-white">
      <main className="article-page w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16 box-border">
        <article className="max-w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            {article.title}
          </h1>

          {!hasImage && article.subtitle?.trim() && (
            <p className="text-lg text-gray-600 italic mb-6">
              {article.subtitle}
            </p>
          )}

          {hasImage && (
            <div className="mb-8">
              <img
                src={imageSrc}
                alt={article.title || 'Article image'}
                className="w-full h-auto rounded-lg shadow-md"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {article.subtitle?.trim() && (
                <p className="mt-3 text-sm text-gray-600 italic text-center">
                  {article.subtitle}
                </p>
              )}
            </div>
          )}

          {(article.author || article.authorWebsite || article.authorName) && (
            <div className="article-byline mb-8 pb-6 border-b border-gray-200">
              <p className="text-sm text-gray-400">
                {(article.author || article.authorName) && <span>{article.author || article.authorName}</span>}
                {(article.author || article.authorName) && (article.authorWebsite || article.website) && <span className="mx-1.5">·</span>}
                {(article.authorWebsite || article.website) && (
                  <a 
                    href={article.authorWebsite || article.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-500 transition-colors no-underline"
                  >
                    {(article.authorWebsite || article.website).replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </p>
            </div>
          )}

          {/* Article Content - prose for typography, responsive */}
          <div className="article-content prose prose-lg prose-gray max-w-none">
            {isHtmlContent ? (
              <div
                className="prose prose-lg prose-gray max-w-none [&_img]:max-w-full [&_img]:h-auto [&_pre]:overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            ) : formattedContent}
          </div>

          <div className="article-footer mt-12 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400 text-center italic">
              Used by permission
            </p>
          </div>

          {showFinishedButton && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              {finished ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Marked as Finished</span>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    onClick={handleFinished}
                    disabled={markingFinished}
                    className="inline-flex items-center px-6 py-3 bg-[#5ABA47] hover:bg-[#4a9939] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-md"
                  >
                    {markingFinished ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Finished
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          {!showFinishedButton && article?.trackId && (
            <div className="mt-8 text-center text-xs text-gray-500 max-w-lg mx-auto px-2 leading-relaxed pb-8">
              To mark this lesson finished, open the <strong className="font-medium text-gray-600">exact link</strong> your mentor sent from Together On Mission. Tracked links include <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">/t/</span> in the path and/or <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">?</span> or <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">#</span> with codes after them. Tap the link in the message—preview screens often open a plain <span className="font-mono text-gray-700 bg-gray-100 px-1 rounded">/article/…</span> URL that cannot record progress.
            </div>
          )}
        </article>
      </main>
    </div>
  );
};

export default Article;
